import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Card,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Collapse,
    Alert, TextField
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { BusLayout } from "./BusLayout";
import { Legend } from "./Legend";
import { processBusData } from "./utils/processBusData";
import { dispatch } from "../../../redux/store";
import { createReservation } from "../../../redux/slices/reservations";
import { getCashierName } from "../../../utils/other";
import { useFormContext, useWatch } from "react-hook-form";
import ProcessingDialog from "../../../components/ProcessingDialog";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSnackbar } from "notistack";
import { postTicketReleaseAPI } from "../../../api/tickets";
import { getStationNamesByAlId } from "../../../api/alltoments";
import { getPassengerDiscountOptions } from "../utils/getPassengerDiscountOptions";
import { discountsOptionLabel } from "../helper";
import Iconify from "../../../components/Iconify";
import { useSelector } from "react-redux";
import uuidv4 from "../../../utils/uuidv4";
import PrivilegeDialog from "../PrivilegeDialog";
import { DIRECTION_NAME } from "../../../constants";
import {getIniLoggedUser} from "../../../helper/session";

dayjs.extend(utc);

const SEAT_STATUS = {
    AVAILABLE: 'AVAILABLE',
    OCCUPIED: 'OCCUPIED',
    RESERVED: 'RESERVED',
    SELECTED: 'SELECTED',
    DISABLED: 'DISABLED',
};

export function BusSeatPicker({
    handleResetForm,
    onClose,
    freeSeats,
    passengersList,
    tripData,
    isReturnSelectionOn,
    isReservationOn,
    onReloadSeats
}) {

    const { seatSelectionEnabled } = useSelector(state => state.userSettings);
    const isSelfSale = tripData.operatorId === getIniLoggedUser()?.id && seatSelectionEnabled
    const { loadingDiscounts } = useSelector(state => state.discounts);
    const { control, setValue, getValues } = useFormContext();
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const [view, setView] = useState('oneWay');
    const { oneWaySeats, returnSeats } = freeSeats
    const [localOneWaySeats, setLocalOneWaySeats] = useState(oneWaySeats);
    const [localReturnSeats, setLocalReturnSeats] = useState(returnSeats);
    const busesData = processBusData(localOneWaySeats, isSelfSale);
    const busesDataReturn = processBusData(localReturnSeats, isSelfSale);

    const [passengers, setPassengers] = useState([]);
    const [activePassengerId, setActivePassengerId] = useState(1);
    const [privilegeDialogProps, setPrivilegeDialogProps] = useState({
        open: false,
        data: null,
        passengerIndex: undefined
    })

    const [allotmentInfo, setAllotmentInfo] = useState([]);
    const [loadingAllotmentInfo, setLoadingAllotmentInfo] = useState(false);

    useEffect(() => {
        const oneWayIds = [...new Set(oneWaySeats?.map(s => s.alId).filter(Boolean) || [])];
        const returnIds = [...new Set(returnSeats?.map(s => s.alId).filter(Boolean) || [])];
        const allIds = [...new Set([...oneWayIds, ...returnIds])];

        if (allIds.length > 0) {
            setLoadingAllotmentInfo(true);
            getStationNamesByAlId(allIds)
                .then(res => {
                    setAllotmentInfo(res.data);
                })
                .catch(err => console.error(err))
                .finally(() => setLoadingAllotmentInfo(false));
        }
    }, [oneWaySeats, returnSeats]);

    useEffect(() => {
        setValue("isReservationDialogOn", true);
    }, []);

    useEffect(() => {
        const onEscCapture = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose?.();
            }
        };

        document.addEventListener("keydown", onEscCapture, true);

        return () => {
            document.removeEventListener("keydown", onEscCapture, true);
        };
    }, [onClose]);

    useEffect(() => {
        if (tripData?.alSeatNumbered === false && isReservationOn === false) {
            enqueueSnackbar('Rezervacija za ovu vrstu putovanja nije moguća.', { variant: 'warning' });
            onClose();
        }
    }, [tripData, isReservationOn, onClose, enqueueSnackbar]);
    useEffect(() => {
        setPassengers(passengersList.map((p, index) => ({
            ...p,
            id: index + 1,
            uid: p.uid || uuidv4(),
            isReturn: directionType === DIRECTION_NAME[1] || false,
            haveOppositeDirection: p.haveOppositeDirection || false,
            seat: p.seat || null,
            vehicleId: p.vehicleId || null,
            ticketId: p.ticketId || null,
            returnSeat: p.returnSeat || null,
            returnVehicleId: p.returnVehicleId || null,
            returnTicketId: p.returnTicketId || null
        })));

        if (passengersList.length > 0) {
            setActivePassengerId(1);
        }
    }, [passengersList]);


    const handleToggleOpposite = (passengerIndex) => {
        const updatedPassengers = [...passengers];
        const passenger = updatedPassengers[passengerIndex];
        updatedPassengers[passengerIndex] = {
            ...passenger,
            haveOppositeDirection: !passenger.haveOppositeDirection,
            discountPCT: null,
        };
        setPassengers(updatedPassengers);
    };

    const handleSelectSeat = (seatId, vehicleId, isReturn = false) => {
        const activePassenger = passengers.find(p => p.id === activePassengerId);
        if (!activePassenger) return;
        const isSeatTakenByOther = passengers.some(p => {
            if (p.id === activePassengerId) return false;
            return isReturn
                ? p.returnSeat === seatId && p.returnVehicleId === vehicleId
                : p.seat === seatId && p.vehicleId === vehicleId
        });
        if (isSeatTakenByOther) {
            const freedPassenger = passengers.find(p => isReturn
                ? p.returnSeat === seatId && p.returnVehicleId === vehicleId
                : p.seat === seatId && p.vehicleId === vehicleId);
            const freed = passengers.map(p => {
                const match = isReturn
                    ? p.returnSeat === seatId && p.returnVehicleId === vehicleId
                    : p.seat === seatId && p.vehicleId === vehicleId;
                if (!match) return p;
                return isReturn
                    ? { ...p, returnSeat: null, returnVehicleId: null, returnTicketId: null }
                    : { ...p, seat: null, vehicleId: null, ticketId: null };
            });
            setPassengers(freed);
            if (freedPassenger) setActivePassengerId(freedPassenger.id);
            return;
        }
        let seatWasSelected = false;
        const updatedPassengers = passengers.map(p => {
            if (p.id === activePassengerId) {
                const seatMatch = isReturn ? p.returnSeat === seatId && p.returnVehicleId === vehicleId
                    : p.seat === seatId && p.vehicleId === vehicleId;

                if (seatMatch) {
                    return isReturn
                        ? { ...p, returnSeat: null, returnVehicleId: null, returnTicketId: null }
                        : { ...p, seat: null, vehicleId: null, ticketId: null };
                }

                seatWasSelected = true;
                return isReturn
                    ? { ...p, returnSeat: seatId, returnVehicleId: vehicleId, returnTicketId: null }
                    : { ...p, seat: seatId, vehicleId: vehicleId, ticketId: null };
            }
            return p;
        });
        setPassengers(updatedPassengers);
        if (seatWasSelected) {
            const currentIndex = updatedPassengers.findIndex(p => p.id === activePassengerId);
            for (let i = 1; i <= updatedPassengers.length; i++) {
                const nextPassenger = updatedPassengers[(currentIndex + i) % updatedPassengers.length];
                const seatMissing = isReturnSelectionOn && view === 'return' ? !nextPassenger.returnSeat : !nextPassenger.seat;
                if (seatMissing) {
                    setActivePassengerId(nextPassenger.id);
                    return;
                }
            }
            // setActivePassengerId(null);
        }
    };
    const getSeatStatus = (seat, vehicleId, isReturn) => {
        if (seat?.status === SEAT_STATUS.DISABLED) {
            return SEAT_STATUS.DISABLED;
        }
        if (seat?.status === SEAT_STATUS.RESERVED) {
            return SEAT_STATUS.RESERVED;
        }
        const passengerOnSeat = passengers.find(p =>
            isReturn
                ? p.returnSeat === seat.id && p.returnVehicleId === vehicleId
                : p.seat === seat.id && p.vehicleId === vehicleId
        );

        if (passengerOnSeat) {
            return SEAT_STATUS.SELECTED;
        }

        return seat.status;
    };

    const allPassengersHaveSeats = useMemo(() => {
        if (isReservationOn) {
            return true;
        }
        return passengers.every(p => p.seat !== null);

    }, [passengers, isReservationOn]);

    const foundDiscounts = useWatch({
        control,
        name: "foundDiscounts"
    })

    const directionType = useWatch({
        control,
        name: "directionType"
    })

    const foundReturnDiscounts = useWatch({
        control,
        name: "foundReturnDiscounts"
    })

    const handleReservation = () => {
        if (isReservationOn) {
            const { name, lastName, phone } = passengers[0];
            if (!name) return enqueueSnackbar("Unesite ime putnika!", { variant: "warning" });
            if (!lastName) return enqueueSnackbar("Unesite prezime putnika!", { variant: "warning" });
            if (!phone) return enqueueSnackbar("Unesite telefon putnika!", { variant: "warning" });
        }

        setLoading(true)

        const findChangedTickets = (initial, final, isReturn) => {
            const initialId = isReturn ? initial.returnTicketId : initial.ticketId;
            const initialSeat = isReturn ? initial.returnSeat : initial.seat;
            const finalSeat = isReturn ? final.returnSeat : final.seat;
            const initialVehicle = isReturn ? initial.returnVehicleId : initial.vehicleId;
            const finalVehicle = isReturn ? final.returnVehicleId : final.vehicleId;

            if (initialId && (initialSeat !== finalSeat || initialVehicle !== finalVehicle)) {
                return initialId;
            }
            return null;
        };

        const oneWayTicketsToRelease = passengersList
            .map((initial, i) => findChangedTickets(initial, passengers[i], false))
            .filter(Boolean);

        const returnTicketsToRelease = isReturnSelectionOn
            ? passengersList
                .map((initial, i) => findChangedTickets(initial, passengers[i], true))
                .filter(Boolean)
            : [];

        const allTicketsToRelease = [...oneWayTicketsToRelease, ...returnTicketsToRelease];

        const releasePromise =
            allTicketsToRelease.length > 0 ? postTicketReleaseAPI(allTicketsToRelease) : Promise.resolve();

        releasePromise
            .then(() => {
                const ticketsPayload = [];
                const returnTripDetails = getValues('returnTrip');
                passengers.forEach(p => {
                    const outboundTicket = {
                        trip: { id: tripData.tripId },
                        linkId: tripData.linkId || null,
                        departureStation: { id: tripData.depStationId },
                        arrivalStation: { id: tripData.arrStationId },
                        passenger: {
                            passengerUid: p.uid,
                            passengerName: p.name,
                            passengerLastName: p.lastName,
                            passengerTelephone: p.phone,
                            passengerEmail: p.email,
                            discountId: p.discountPCT?.ticketTypeId || 1
                        },
                        isOppositeTicket: !!p.haveOppositeDirection,
                        isReturnTicket: false,
                        seatReserved: p.seat,
                        vehicleId: p.vehicleId,
                        ticketType: { id: p.discountPCT?.ticketTypeId || 1 },
                        journeyDate: dayjs.utc(tripData.departureTripTime).format('YYYY-MM-DDTHH:mm:ss')
                    };
                    ticketsPayload.push(outboundTicket);
                    if (p.isReturn) {
                        const returnTicket = {
                            ...outboundTicket,
                            isReturnTicket: true,
                            trip: { id: returnTripDetails ? returnTripDetails.tripId : null },
                            seatReserved: p.returnSeat,
                            vehicleId: p.returnVehicleId,
                            journeyDate: returnTripDetails ? dayjs.utc(returnTripDetails.departureTime).format('YYYY-MM-DDTHH:mm:ss') : null
                        };
                        ticketsPayload.push(returnTicket);
                    }
                });
                if (ticketsPayload.length === 0) {
                    onClose();
                    return Promise.reject('No changes to save.');
                }

                const finalPayload = {
                    cashier: getCashierName(),
                    isGroupBooking: isReservationOn,
                    isTemporary: true,
                    returnDate: isReturnSelectionOn
                        ? dayjs.utc(returnTripDetails.departureTime).format('YYYY-MM-DDTHH:mm:ss')
                        : null,
                    returnTripId: isReturnSelectionOn ? returnTripDetails.tripId : null,
                    tickets: ticketsPayload,
                };

                return dispatch(createReservation(finalPayload)).unwrap();
            })
            .then(allNewTickets => {
                const finalPassengersState = passengers.map(p => {
                    const newOutboundTicket = allNewTickets.find(t => !t.isReturnTicket && t.passenger.passengerUid === p.uid);
                    const newReturnTicket = allNewTickets.find(t => t.isReturnTicket && t.passenger.passengerUid === p.uid);

                    return {
                        ...p,
                        ticketId: newOutboundTicket ? newOutboundTicket.id : p.ticketId,
                        returnTicketId: newReturnTicket ? newReturnTicket.id : p.returnTicketId,
                    };
                });

                setValue('passengers', finalPassengersState);
                enqueueSnackbar('Rezervacija uspešno sačuvana!', { variant: 'success' });
                if (isReservationOn) {
                    handleResetForm();
                }
                onClose();
            })
            .catch(e => {
                if (e !== 'No changes to save.') {
                    enqueueSnackbar(
                        'Jedno ili više traženih sedišta je u međuvremenu zauzeto.',
                        { variant: 'warning' }
                    );

                    if (onReloadSeats) {
                        onReloadSeats();
                    }
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleTimerEnd = (seatId, vehicleId) => {
        const updateSeatStatus = (seats) => seats.map(seat =>
            (seat.vehicleId === vehicleId && seat.seat === seatId)
                ? { ...seat, status: SEAT_STATUS.AVAILABLE, reserved: false, reservationExpireSec: null }
                : seat
        );
        setLocalOneWaySeats(updateSeatStatus);
        setLocalReturnSeats(updateSeatStatus);

        const updatedPassengers = passengers.map(p => {
            if (p.seat === seatId && p.vehicleId === vehicleId) {
                return { ...p, seat: null, vehicleId: null, ticketId: null };
            }
            if (p.returnSeat === seatId && p.returnVehicleId === vehicleId) {
                return { ...p, returnSeat: null, returnVehicleId: null, returnTicketId: null };
            }
            return p;
        });

        setPassengers(updatedPassengers);
        setValue('passengers', updatedPassengers);
        enqueueSnackbar(`Rezervacija za sedište ${seatId} je istekla.`, { variant: 'warning' });
    };

    if (loading) {
        return <ProcessingDialog loading={loading} />;
    }
    return (
        <Box sx={{
            width: '100%',
            p: { xs: 2, sm: 3 },
            margin: 'auto',
            mt: 0,
            borderRadius: '20px',
            background: '#ffffff',
            overflow: 'auto'
        }}>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Iconify icon="mdi:seat-outline" width={24} height={24} color="primary.main" />
                        <Typography variant="h6" fontWeight="bold">
                            Biranje sedišta u autobusu
                        </Typography>
                        <Divider flexItem sx={{ ml: 2 }} />
                    </Stack>
                    {isReservationOn && (
                        <Stack sx={{ mb: 2 }}>
                            <Typography fontSize="14px" fontWeight="700" gutterBottom>
                                Podaci za rezervaciju
                            </Typography>
                            <Stack direction="row" gap={1} spacing={2} sx={{ mt: 1 }}>
                                <TextField
                                    disabled={passengers[0]?.discountPCT?.documentValidation}
                                    label="Ime*"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={passengers[0]?.name || ''}
                                    onChange={(e) => {
                                        const updated = [...passengers];
                                        updated[0] = { ...updated[0], name: e.target.value };
                                        setPassengers(updated);
                                    }}
                                />
                                <TextField
                                    disabled={passengers[0]?.discountPCT?.documentValidation}
                                    label="Prezime*"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={passengers[0]?.lastName || ''}
                                    onChange={(e) => {
                                        const updated = [...passengers];
                                        updated[0] = { ...updated[0], lastName: e.target.value };
                                        setPassengers(updated);
                                    }}
                                />
                                <TextField
                                    label="Telefon*"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={passengers[0]?.phone || ''}
                                    onChange={(e) => {
                                        const updated = [...passengers];
                                        updated[0] = { ...updated[0], phone: e.target.value };
                                        setPassengers(updated);
                                    }}
                                />
                                <TextField
                                    label="Mejl"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={passengers[0]?.email || ''}
                                    onChange={(e) => {
                                        const updated = [...passengers];
                                        updated[0] = { ...updated[0], email: e.target.value };
                                        setPassengers(updated);
                                    }}
                                />
                            </Stack>

                            <Divider variant="dashed" sx={{ my: 2 }} />
                        </Stack>
                    )}

                    {loadingAllotmentInfo ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : allotmentInfo.length > 0 ? (
                        <Box sx={{ mb: 2 }}>
                            <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                                Informacije o sedištima
                            </Alert>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Polazna</TableCell>
                                            <TableCell>Dolazna</TableCell>
                                            <TableCell>Sedišta</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allotmentInfo.map((info, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{info.depStationName}</TableCell>
                                                <TableCell>{info.arrStationName}</TableCell>
                                                <TableCell>{info.seatFrom}-{info.seatTo}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : null}

                    {isReturnSelectionOn && (
                        <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
                            <Button variant={view === 'oneWay' ? 'contained' : 'outlined'}
                                onClick={() => setView('oneWay')}>Polazak</Button>
                            <Button variant={view === 'return' ? 'contained' : 'outlined'}
                                onClick={() => setView('return')}>Povratak</Button>
                        </ButtonGroup>
                    )}
                    {tripData?.alSeatNumbered && <AnimatePresence mode="wait">
                        <motion.div
                            key={view}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            {view === 'oneWay' && busesData.map(bus => (
                                <BusLayout key={bus.id} bus={bus}
                                    getSeatStatus={(seat, vId) => getSeatStatus(seat, vId, false)}
                                    handleSelectSeat={(sId, vId) => handleSelectSeat(sId, vId, false)}
                                    onTimerEnd={handleTimerEnd} />
                            ))}
                            {view === 'return' && busesDataReturn.map(bus => (
                                <BusLayout key={bus.id} bus={bus}
                                    getSeatStatus={(seat, vId) => getSeatStatus(seat, vId, true)}
                                    handleSelectSeat={(sId, vId) => handleSelectSeat(sId, vId, true)}
                                    onTimerEnd={handleTimerEnd} />
                            ))}
                        </motion.div>
                    </AnimatePresence>}
                    <Legend />
                </Grid>

                <Grid item xs={12}>
                    <motion.div initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}>
                        <Card sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent="center"
                                alignItems="center"
                                sx={{ mb: 2 }}
                            >
                                <motion.div style={{ width: 200 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                        onClick={onClose}
                                        variant="outlined"
                                        color="inherit"
                                        fullWidth
                                        sx={{
                                            borderRadius: '12px',
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Odustani
                                    </Button>
                                </motion.div>

                                <motion.div style={{ width: 200 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Button
                                        onClick={handleReservation}
                                        variant="contained"
                                        color="primary"
                                        disabled={!allPassengersHaveSeats}
                                        fullWidth
                                        sx={{
                                            borderRadius: '12px',
                                            px: 4,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: '1rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Rezerviši
                                    </Button>
                                </motion.div>
                            </Stack>


                            <Divider variant="dashed" sx={{ my: 1 }} />

                            <Typography fontSize="14px" fontWeight="700" gutterBottom>
                                Putnici i povlastice
                            </Typography>

                            {/* GRID SA PUTNICIMA: 3 u redu na md, 2 na sm */}
                            <Grid container spacing={2}>
                                {passengers.map((p, index) => {
                                    const isActive = activePassengerId === p.id;
                                    const busOdlazak = p.vehicleId ? busesData.find(b => b.id === p.vehicleId) : null;
                                    const busPovratak = p.returnVehicleId ? busesDataReturn.find(b => b.id === p.returnVehicleId) : null;

                                    const odlazakText =
                                        p.seat && busOdlazak ? `Odlazak: ${p.seat} (${busOdlazak.plate})` : 'Odlazak: Nije izabrano';
                                    const povratakText = isReturnSelectionOn
                                        ? p.returnSeat && busPovratak
                                            ? `Povratak: ${p.returnSeat} (${busPovratak.plate})`
                                            : 'Povratak: Nije izabrano'
                                        : '';

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={p.id}>
                                            <motion.div layout transition={{ duration: 0.2 }}>
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        borderColor: isActive ? 'primary.main' : 'divider',
                                                        transition: 'border-color 0.3s',
                                                    }}
                                                >
                                                    <ListItemButton onClick={() => setActivePassengerId(p.id)}
                                                        sx={{ zIndex: 1 }}>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="subtitle2" fontWeight="bold">
                                                                    Putnik {p.id}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <>
                                                                    <Typography variant="body2" color="text.secondary"
                                                                        component="span" display="block">
                                                                        {odlazakText}
                                                                    </Typography>
                                                                    {isReturnSelectionOn && (
                                                                        <Typography variant="body2"
                                                                            color="text.secondary"
                                                                            component="span" display="block">
                                                                            {povratakText}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            }
                                                        />
                                                    </ListItemButton>

                                                    <Box sx={{
                                                        p: 2,
                                                        pt: 0,
                                                        borderTop: '1px solid',
                                                        borderColor: 'divider',
                                                        flexGrow: 1
                                                    }}>
                                                        {(tripData?.depCountryName === 'Srbija' || tripData.arrCountryName === 'Srbija') && (
                                                            <Autocomplete
                                                                key={index}
                                                                fullWidth
                                                                disabled={tripData.externalSale}
                                                                options={getPassengerDiscountOptions(
                                                                    passengers[index],
                                                                    directionType,
                                                                    foundReturnDiscounts,
                                                                    foundDiscounts
                                                                )}
                                                                getOptionLabel={(option) => discountsOptionLabel(option)}
                                                                onChange={(event, value) => {
                                                                    const currPassenger = {
                                                                        ...passengers[index],
                                                                        discountPCT: value
                                                                    };
                                                                    const arr = [...passengers];
                                                                    arr[index] = currPassenger;
                                                                    setPassengers(arr);
                                                                    if (value?.documentValidation === true) {
                                                                        setPrivilegeDialogProps({
                                                                            open: true,
                                                                            data: currPassenger,
                                                                            passengerIndex: index
                                                                        });
                                                                    }
                                                                }}
                                                                value={passengers[index]?.discountPCT || null}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Izaberite povlasticu / popust"
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            size: 'small',
                                                                            startAdornment: loadingDiscounts ? (
                                                                                <CircularProgress size={16} />
                                                                            ) : (
                                                                                <Iconify width={16}
                                                                                    icon={'mdi:discount-outline'} />
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        )}

                                                        {passengers[index]?.discountPCT?.documentValidation && (
                                                            <Stack spacing={0.5} mt={1}>
                                                                {passengers[index]?.name && (
                                                                    <Typography variant="caption">
                                                                        <strong>Ime:</strong> {passengers[index]?.name}
                                                                    </Typography>
                                                                )}
                                                                {passengers[index]?.lastName && (
                                                                    <Typography variant="caption">
                                                                        <strong>Prezime:</strong> {passengers[index]?.lastName}
                                                                    </Typography>
                                                                )}
                                                                {passengers[index]?.cardNumber && (
                                                                    <Typography variant="caption">
                                                                        <strong>Broj
                                                                            karte:</strong> {passengers[index]?.cardNumber}
                                                                    </Typography>
                                                                )}
                                                                {passengers[index]?.clientIdentity && (
                                                                    <Typography variant="caption">
                                                                        <strong>Broj
                                                                            povlastice:</strong> {passengers[index]?.clientIdentity}
                                                                    </Typography>
                                                                )}
                                                            </Stack>
                                                        )}

                                                        {passengers?.length > 1 && (
                                                            <FormControlLabel
                                                                sx={{ mt: 0.5 }}
                                                                control={
                                                                    <Checkbox
                                                                        checked={!!passengers[index]?.haveOppositeDirection}
                                                                        onChange={() => handleToggleOpposite(index)}
                                                                    />
                                                                }
                                                                label="Suprotan smer"
                                                            />
                                                        )}
                                                    </Box>
                                                </Card>
                                            </motion.div>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Card>
                    </motion.div>
                </Grid>

            </Grid>

            {privilegeDialogProps.open &&
                <PrivilegeDialog
                    sales={foundDiscounts}
                    open={privilegeDialogProps.open}
                    passengerData={privilegeDialogProps.data}
                    onClose={() => {
                        setPrivilegeDialogProps({
                            open: false,
                            data: privilegeDialogProps.data,
                            passengerIndex: undefined
                        })
                    }}
                    onSubmit={(parsedPassengerData) => {
                        if (privilegeDialogProps.passengerIndex !== undefined) {
                            const updatedPassengers = [...passengers];
                            updatedPassengers[privilegeDialogProps.passengerIndex] = {
                                ...updatedPassengers[privilegeDialogProps.passengerIndex],
                                ...parsedPassengerData
                            };
                            setPassengers(updatedPassengers);
                        }
                        setPrivilegeDialogProps({ open: false, data: null, passengerIndex: undefined });
                    }}

                    passengerIndex={privilegeDialogProps.passengerIndex}
                />
            }
        </Box>
    );
}
